const TFS_HIERARCHY_LINK = 'System.LinkTypes.Hierarchy';

export default class LinkMappingService {
	constructor(http) {
		this.http = http;
		this.hierarchy = {};
	}

	getData() {
		const self = this;

		return new Promise(function (resolve, reject) {
			self.http.get(self.backendUrl + '/mappings/links/availableValues').then(
				(availableValues) => {
					self.http.get(self.backendUrl + '/mappings/links').then(
						(mappings) => {
							resolve(self.shapeDataFromWebService(
								availableValues.data,
								mappings.data.LinkTypesMappings
							));
						},
						(err) => {
							reject(err);
						}
					);
				},
				(err) => {
					reject(err);
				}
			);
		});
	}

	shapeDataFromWebService(availableValues, mappings) {
		function createItem(kind, title, hasInvertedVersion, key, isInverted) {
			return {
				kind: kind,
				title: title,
				label: title,
				invertedTitle: title.split("/").reverse().join(" / "),
				id: key + '_' + (isInverted),
				key: key,
				inverted: isInverted,
				hasInvertedVersion: hasInvertedVersion,
				visible: true
			}
		};

		let items = {};
		this.hierarchy = availableValues.Hierarchy;
		Array.prototype.forEach.call(availableValues.JiraValues, el => {
			let item = createItem(
				'JIRA',
				el.LabelA + ' / ' + el.LabelB,
				el.LabelA !== el.LabelB,
				el.JiraLinkType,
				false
			);
			items[item.id] = item;
		});

		// Create items for TFS types. For each item we need to create inverted version
		Array.prototype.forEach.call(availableValues.TfsValues, el => {
			let item = (createItem(
				'TFS',
				el.LabelA + ' / ' + el.LabelB,
				el.LabelA !== el.LabelB,
				el.TfsLinkType,
				false
			));

			items[item.id] = item;

			if (el.LabelA !== el.LabelB) {
				let invertedItem = (createItem(
					'TFS',
					el.LabelB + ' / ' + el.LabelA,
					el.LabelA !== el.LabelB,
					el.TfsLinkType,
					true
				));
				items[invertedItem.id] = invertedItem;
			}
		});

		return this.hideListItemsAndEnrichMappings(mappings, items);
	}

	/*
	 * Hide jira / tfs item if mapping for this item is already created
	 * Also enhance informations about the mapped link
	 */
	hideListItemsAndEnrichMappings(mappings, items) {
		mappings.forEach(mapping => {
			// For a mapping find related JiraValues and TfsValues
			Object.values(items).forEach(item => {
				if (item.key === mapping.JiraLinkType) {
					mapping.jiraValue = item;
				} else if (item.key === mapping.TfsLinkType && mapping.IsLinkDirectionInverted === item.inverted) {
					mapping.tfsValue = item;
				}
			});

			mapping.duringProcessing = false;

			if (mapping.jiraValue === undefined) {
				mapping.jiraTitle = mapping.JiraLinkType;
			} else {
				mapping.jiraId = mapping.jiraValue.id;
				mapping.jiraTitle = mapping.jiraValue.title;
			}

			if (mapping.tfsValue === undefined) {
				mapping.tfsTitle = mapping.TfsLinkType;
			} else {
				mapping.tfsTitle = mapping.tfsValue.title;
				mapping.tfsId = mapping.tfsValue.id;
				mapping.invertedTitle = mapping.tfsValue.invertedTitle;
			}
		});

		//filtering non-existing links e.g. deleted in jira/tfs
		let splitMappings = this.splitMappingsToFindThoseToRemove(mappings);
		mappings = splitMappings[0];
		let mappingsToRemove = splitMappings[1];

		mappingsToRemove.forEach(mapping => { 
			this.removeSingleMappingById(mapping.Id);
		});

		mappings = mappings.sort(function (a, b) {
			return a.jiraTitle.toString() > b.jiraTitle.toString() ? -1 : 1;
		});

		Object.values(items).forEach(item => {
			const foundMappings = Array.prototype.filter.call(
				mappings,
				(mapping) => mapping.jiraId === item.id || mapping.tfsId === item.id || (mapping.tfsValue !== undefined && mapping.tfsValue.key == item.key)
			);
			// visible only if its not inverted version and not yet mapped
			item.visible = foundMappings.length === 0 && item.inverted == false;
			if (this.hierarchy.Enabled && (TFS_HIERARCHY_LINK === item.key || this.hierarchy.JiraLinkId === item.key)) {
				item.visible = false;
			}
		});

		if (this.hierarchy.Enabled) {
			mappings = mappings.filter((mapping) => mapping.jiraValue.key !== this.hierarchy.JiraLinkId && mapping.TfsLinkType !== TFS_HIERARCHY_LINK);
		}

		return {
			hierarchy: this.hierarchy,
			available: items,
			mappings: mappings
		};
	}

	switchTfsMapping(mappingRow, availableValues, mappings) {
		const self = this;
		return new Promise(function (resolve, reject) {
			const oppositeMapping = Array.prototype.filter.call(Object.values(availableValues), (el) =>
				el.kind === 'TFS' && el.key === mappingRow.tfsValue.key && el.id !== mappingRow.tfsValue.id
			);
			if (oppositeMapping.length === 0) {
				resolve(null);
			} else {
				self.removeMapping(availableValues, mappings, mappingRow, false).then(
					(data) => {
						self.addMapping(mappingRow.jiraValue, oppositeMapping[0], data.available, data.mappings).then(
							(mappingData) => {
								resolve({
									mappings: mappingData,
									available: data.available
								});
							},
							(err) => reject(err)
						);
					},
					(err) => {
						reject(err);
					}
				);
			}
		});
	}


	addMapping(jiraValue, tfsValue, availableValues, mappings) {
		const self = this;

		return new Promise(function (resolve, reject) {
			self.http.post(self.backendUrl + '/mappings/links/values', {
				JiraLinkType: jiraValue.key,
				TfsLinkType: tfsValue.key,
				IsLinkDirectionInverted: tfsValue.inverted
			}).then(
				(data) => {
					mappings.push(data.data);
					const enriched = self.hideListItemsAndEnrichMappings(mappings, availableValues);
					resolve(enriched.mappings);
				},
				(err) => {
					reject(err);
				}
			)
		});
	}

	removeMapping(availableValues, mappings, mappingObject, callHideItems) {
		const self = this;
		return new Promise(function (resolve, reject) {
			self.http.delete(self.backendUrl + '/mappings/links/values/' + mappingObject.Id).then(
				(data) => {
					mappings = Array.prototype.filter.call(mappings, (el) => el.Id !== mappingObject.Id);
					if (callHideItems) {
						resolve(self.hideListItemsAndEnrichMappings(
							mappings,
							availableValues
						));
					} else {
						resolve({
							mappings: mappings,
							available: availableValues
						})
					}
				},
				(err) => {
					reject(err);
				}
			)
		});
	}

	removeSingleMappingById(mappingId) {
		const self = this;
		self.http.delete(self.backendUrl + '/mappings/links/values/' + mappingId).then(
			() => { },
			(err) => {
				reject(err);
			}
		);
	}

	splitMappingsToFindThoseToRemove(mappings)  {
		return this.partition(mappings, (mapping) => {return mapping.jiraValue != undefined && mapping.tfsValue != undefined});
	}

	partition(arr, filter) {
		return arr.reduce(
		  (accumulator, currentElement) => {
			accumulator[filter(currentElement) ? 0 : 1].push(currentElement);
			return accumulator;
		  }, [[], []]);
	  }
}


// WEBPACK FOOTER //
// ./src/service/links.js